import _ from 'lodash';

import { normalizeNumericEnumValue } from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';

import { CATEGORY_VALUES } from './constants';

const {
  SecurityEventScopeCategory,
  SecurityEventScopeType,
  SiemConnectorType,
  SiemDestinationAuthorizationType,
  SiemDestinationStatus,
} = CoreEnums;

const allowedCategories = new Set(CATEGORY_VALUES);

export function enumMember(enumObject, value) {
  const key = Object.keys(enumObject).find((name) => enumObject[name] === value);
  return key ? _.kebabCase(key) : undefined;
}

export const SCOPE_FORM_GLOBAL = enumMember(
  SecurityEventScopeCategory,
  SecurityEventScopeCategory.Global,
);
export const SCOPE_FORM_ENVIRONMENT = enumMember(
  SecurityEventScopeType,
  SecurityEventScopeType.Environment,
);
export const SCOPE_CATEGORY_SCOPED = enumMember(
  SecurityEventScopeCategory,
  SecurityEventScopeCategory.Scoped,
);

export function normalizeCollection(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

export function slicePage(rows, page, limit) {
  const list = Array.isArray(rows) ? rows : [];
  const size = limit > 0 ? limit : list.length || 1;
  const current = page > 0 ? page : 1;
  const start = (current - 1) * size;
  return list.slice(start, start + size);
}

export function filterByName(rows, search) {
  const term = String(search || '')
    .trim()
    .toLowerCase();
  if (!term) return rows || [];
  return (rows || []).filter((row) =>
    String(row?.name || '')
      .toLowerCase()
      .includes(term),
  );
}

export function filterByActive(rows, activeFilter) {
  const value = unwrapFilterValue(activeFilter);
  if (value !== true && value !== false && value !== 'true' && value !== 'false') {
    return rows || [];
  }
  const wantActive = value === true || value === 'true';
  return (rows || []).filter((row) => (row?.isActive === true) === wantActive);
}

function unwrapFilterValue(filter) {
  if (filter && typeof filter === 'object' && 'value' in filter) return filter.value;
  return filter;
}

export function filterByStatus(rows, statusFilter) {
  const value = unwrapFilterValue(statusFilter);
  const want = Number(value);
  if (!Object.values(SiemDestinationStatus).includes(want)) {
    return rows || [];
  }
  return (rows || []).filter((row) => destinationStatus(row?.status) === want);
}

export function filterByConnector(rows, connectorFilter) {
  const value = unwrapFilterValue(connectorFilter);
  const want = Number(value);
  if (!Object.values(SiemConnectorType).includes(want)) {
    return rows || [];
  }
  return (rows || []).filter((row) => normalizeConnectorType(row?.connectorType) === want);
}

export function sortRows(rows, orderBy) {
  if (!orderBy) return rows || [];
  const [field, direction] = String(orderBy).split(' ');
  const dir = direction === 'desc' ? -1 : 1;
  return [...(rows || [])].sort((left, right) => {
    const a = left?.[field];
    const b = right?.[field];
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return (a - b) * dir;
    return (
      String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true }) * dir
    );
  });
}

export function destinationStatus(status) {
  return Number(status) === SiemDestinationStatus.Ready
    ? SiemDestinationStatus.Ready
    : SiemDestinationStatus.Incomplete;
}

export function isDestinationEffective(destination) {
  return (
    destination?.isActive === true &&
    destinationStatus(destination.status) === SiemDestinationStatus.Ready
  );
}

export function destinationCounts(subscriptionId, destinations) {
  const rows = (destinations || []).filter((row) => row?.subscriptionId === subscriptionId);
  return {
    destinationCount: rows.length,
    readyActiveDestinationCount: rows.filter(isDestinationEffective).length,
  };
}

export function subscriptionDeliveryState(subscription, destinations) {
  if (subscription?.isActive !== true) return 'inactive';
  const counts = destinationCounts(subscription.id, destinations);
  return counts.readyActiveDestinationCount > 0 ? 'effective' : 'missingDestination';
}

export function decorateSubscription(item, destinations, environmentNames = {}) {
  const counts = destinationCounts(item?.id, destinations);
  const scoped = item?.scope?.category === SCOPE_CATEGORY_SCOPED;
  return {
    ...item,
    isActive: item?.isActive === true,
    ...counts,
    deliveryState: subscriptionDeliveryState(item, destinations),
    environmentName: scoped ? environmentNames[item?.scope?.id] || item?.scope?.id : null,
  };
}

export function normalizeCatalogue(values) {
  const source = Array.isArray(values) && values.length ? values : CATEGORY_VALUES;
  const unique = [
    ...new Set(
      source.map((value) => Number(value)).filter((value) => allowedCategories.has(value)),
    ),
  ];
  unique.sort((a, b) => a - b);
  return unique.length ? unique : [...CATEGORY_VALUES];
}

export function normalizeCategoriesForSubmit(allCategories, categories) {
  if (allCategories === true) return [];
  const unique = [
    ...new Set(
      (categories || [])
        .map((value) => Number(value))
        .filter((value) => allowedCategories.has(value)),
    ),
  ];
  unique.sort((a, b) => a - b);
  return unique;
}

export function unwrapId(value) {
  if (value && typeof value === 'object') return value.value || value.id || value.key;
  return value;
}

export function buildSubscriptionSubmitData(payload) {
  const environmentId = unwrapId(payload?.environmentId);
  const scope =
    payload?.scopeCategory === SCOPE_FORM_ENVIRONMENT
      ? { category: SCOPE_CATEGORY_SCOPED, type: SCOPE_FORM_ENVIRONMENT, id: environmentId }
      : { category: SCOPE_FORM_GLOBAL };
  return {
    name: String(payload?.name || '').trim(),
    isActive: payload?.isActive === true,
    scope,
    categories: normalizeCategoriesForSubmit(payload?.allCategories === true, payload?.categories),
  };
}

export function normalizeAuthorizationType(value) {
  return normalizeNumericEnumValue(
    SiemDestinationAuthorizationType,
    value,
    SiemDestinationAuthorizationType.NoAuth,
  );
}

export function normalizeParameterPosition(value) {
  const text = String(value ?? '')
    .trim()
    .toLowerCase();
  if (text === 'header' || text === '1') return 'header';
  if (text === 'url' || text === '2') return 'url';
  return text;
}

function hasText(value) {
  if (typeof value === 'string') return value.trim() !== '';
  return value != null && value !== '';
}

export function normalizeParameters(parameters) {
  if (!Array.isArray(parameters)) return [];
  return parameters
    .map((row) => {
      const position = normalizeParameterPosition(row?.position);
      const name = String(row?.name || '').trim();
      if (!name || (position !== 'header' && position !== 'url')) return null;
      const next = {
        position,
        name,
        confidential: row?.confidential === true,
      };
      if (hasText(row?.value)) next.value = row.value;
      return next;
    })
    .filter(Boolean);
}

export function normalizeConnectorType(value) {
  return normalizeNumericEnumValue(SiemConnectorType, value, SiemConnectorType.GenericHttps);
}

function optionalText(target, key, value) {
  if (!hasText(value)) return;
  target[key] = String(value).trim();
}

function buildHttpsConfigurations(payload) {
  const auth = normalizeAuthorizationType(payload?.authorizationType);
  const configurations = {
    url: String(payload?.url || '').trim(),
    authorizationType: enumMember(SiemDestinationAuthorizationType, auth),
  };

  if (
    auth === SiemDestinationAuthorizationType.ApiKeyInHeader ||
    auth === SiemDestinationAuthorizationType.ApiKeyInQueryParameters
  ) {
    if (hasText(payload?.keyName)) configurations.keyName = String(payload.keyName).trim();
    if (hasText(payload?.keyValue)) configurations.keyValue = payload.keyValue;
  }
  if (auth === SiemDestinationAuthorizationType.BearerToken && hasText(payload?.token)) {
    configurations.token = payload.token;
  }
  if (auth === SiemDestinationAuthorizationType.BasicAuth) {
    if (hasText(payload?.username)) configurations.username = String(payload.username).trim();
    if (hasText(payload?.password)) configurations.password = payload.password;
  }
  if (Array.isArray(payload?.parameters)) {
    configurations.parameters = normalizeParameters(payload.parameters);
  }
  return configurations;
}

function buildSplunkConfigurations(payload) {
  const configurations = {
    endpoint: String(payload?.endpoint || '').trim(),
  };
  if (hasText(payload?.hecToken)) configurations.token = payload.hecToken;
  optionalText(configurations, 'index', payload?.index);
  optionalText(configurations, 'source', payload?.source);
  optionalText(configurations, 'sourceType', payload?.sourceType);
  optionalText(configurations, 'host', payload?.host);
  return configurations;
}

function buildEventHubConfigurations(payload) {
  const configurations = {};
  if (hasText(payload?.connectionString))
    configurations.connectionString = payload.connectionString;
  optionalText(configurations, 'eventHubName', payload?.eventHubName);
  return configurations;
}

export function buildDestinationSubmitData(payload, { subscriptionId } = {}) {
  const connectorType = normalizeConnectorType(payload?.connectorType);
  const configurations =
    connectorType === SiemConnectorType.SplunkHec
      ? buildSplunkConfigurations(payload)
      : connectorType === SiemConnectorType.EventHub
      ? buildEventHubConfigurations(payload)
      : buildHttpsConfigurations(payload);

  return {
    name: String(payload?.name || '').trim(),
    subscriptionId,
    isActive: payload?.isActive === true,
    connectorType,
    configurations,
  };
}

export function prepareSubscriptionFields(fields, { item } = {}) {
  if (!item?.id) return fields;
  const allCategories = !Array.isArray(item.categories) || item.categories.length === 0;
  const scopeCategory =
    item.scope?.category === SCOPE_CATEGORY_SCOPED ? SCOPE_FORM_ENVIRONMENT : SCOPE_FORM_GLOBAL;
  const environmentId = item.scope?.id
    ? { value: item.scope.id, label: item.environmentName || item.scope.id }
    : undefined;

  return fields.map((field) => {
    if (field.name === 'scopeCategory') return { ...field, value: scopeCategory };
    if (field.name === 'environmentId') return { ...field, value: environmentId };
    if (field.name === 'allCategories') {
      return {
        ...field,
        value: allCategories,
        checked: allCategories,
        defaultChecked: allCategories,
      };
    }
    if (field.name === 'categories') {
      return { ...field, value: allCategories ? [] : item.categories };
    }
    if (field.name === 'isActive') {
      const active = item.isActive === true;
      return { ...field, value: active, checked: active, defaultChecked: active };
    }
    return field;
  });
}

export function prepareDestinationFields(fields, { item } = {}) {
  if (!item?.id) return fields;
  const config = item.configurations || {};
  const auth = normalizeAuthorizationType(config.authorizationType);
  const parameters = (config.parameters || []).map((row) => ({
    position: normalizeParameterPosition(row.position),
    name: row.name,
    confidential: row.confidential === true,
    value: row.confidential === true ? undefined : row.value,
  }));

  const connectorType = normalizeConnectorType(item.connectorType);

  return fields.map((field) => {
    if (field.name === 'connectorType') return { ...field, value: connectorType };
    if (field.name === 'url') return { ...field, value: config.url };
    if (field.name === 'authorizationType') return { ...field, value: auth };
    if (field.name === 'keyName') return { ...field, value: config.keyName };
    if (field.name === 'username') return { ...field, value: config.username };
    if (field.name === 'parameters') return { ...field, value: parameters };
    if (field.name === 'endpoint') return { ...field, value: config.endpoint };
    if (field.name === 'index') return { ...field, value: config.index };
    if (field.name === 'source') return { ...field, value: config.source };
    if (field.name === 'sourceType') return { ...field, value: config.sourceType };
    if (field.name === 'host') return { ...field, value: config.host };
    if (field.name === 'eventHubName') return { ...field, value: config.eventHubName };
    if (
      field.name === 'keyValue' ||
      field.name === 'token' ||
      field.name === 'password' ||
      field.name === 'hecToken' ||
      field.name === 'connectionString'
    ) {
      return { ...field, value: undefined };
    }
    if (field.name === 'isActive') {
      const active = item.isActive === true;
      return { ...field, value: active, checked: active, defaultChecked: active };
    }
    return field;
  });
}
